import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Tag, theme, Empty, Button } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { beautifyKey, convertKeyToCamelCase, formatUkDateTime, isDateTimeDtoField } from '../../../utils/functions';
import { FilePathCard, TemplateContentPreview } from '../utils/renderUIComponents';
import ApiErrorAlert from '../../common/ApiErrorAlert';

// Same set of sensitive field names masked with Input.Password in Create/Edit forms
// (see renderUIComponents.js renderField) — kept in sync so View mode never leaks secrets.
const PASSWORD_FIELD_NAMES = new Set([
    'Password', 'NifiPassword', 'SupersetPassword', 'SftpPassword', 'DatabaseConnectionString', 'FhirServerPassword',
]);

const MaskedValue = ({ value }) => {
    const [revealed, setRevealed] = useState(false);
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: 'monospace', letterSpacing: 2 }}>
                {revealed ? String(value) : '•'.repeat(Math.min(String(value).length, 16) || 8)}
            </span>
            <Button
                type="text"
                size="small"
                icon={revealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                onClick={() => setRevealed((prev) => !prev)}
            />
        </span>
    );
};

// Read-only field grid for View mode. Long-text fields take a full row; everything
// else gets a third-width column, matching promedhis's ViewForm layout convention.
const FULL_WIDTH_FIELDS = new Set([
    'Notes', 'Comments', 'Description', 'Address', 'StreetAddress', 'Method', 'Interpretation', 'History',
    'TemplateContent',
]);

// Cosmetic: show "Patient" instead of "Patient Id" in View mode for entity refs.
const cleanLabel = (fieldName) => (
    fieldName.endsWith('Id') && fieldName !== 'Id' ? beautifyKey(fieldName.slice(0, -2)) : beautifyKey(fieldName)
);

const ViewField = ({ label, children, token }) => (
    <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 11, color: token.colorTextTertiary, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
            {label}
        </div>
        <div style={{ fontSize: 14, color: token.colorText, lineHeight: 1.5, minHeight: 22 }}>
            {children}
        </div>
    </div>
);

const renderViewValue = (fieldName, fieldProps, value, userInfo, t) => {
    if (fieldName === 'FilePath' || fieldName === 'MatFilePath') {
        return <FilePathCard filePath={value} emptyText={t('Not available')} userInfo={userInfo} />;
    }
    if (fieldName === 'TemplateContent' || fieldProps?.type === 'TemplateContent') {
        if (value === null || value === undefined || value === '') {
            return <span style={{ color: '#bfbfbf' }}>-</span>;
        }
        return <TemplateContentPreview content={value} />;
    }
    if (value === null || value === undefined || value === '') return <span style={{ color: '#bfbfbf' }}>-</span>;
    if (PASSWORD_FIELD_NAMES.has(fieldName)) return <MaskedValue value={value} />;
    if (isDateTimeDtoField(fieldProps)) return <span>{formatUkDateTime(value)}</span>;
    if (typeof value === 'boolean') {
        return <Tag color={value ? 'success' : 'default'}>{value ? t('Yes') : t('No')}</Tag>;
    }
    if (Array.isArray(value)) {
        return value.length > 0
            ? <span>{value.length} {t('item(s)')}</span>
            : <span style={{ color: '#bfbfbf' }}>-</span>;
    }
    if (fieldProps?.type === 'Decimal' && typeof value === 'number') return <span>{value.toLocaleString()}</span>;
    return <span>{String(value)}</span>;
};

const ViewForm = ({ entity, entityDtoConfig, fieldsToHide, error }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const userInfo = useSelector((state) => state.userLogin?.userInfo);

    if (!entity && !error) {
        return <Empty description={t('No data available')} style={{ marginTop: 40 }} />;
    }

    if (!entity && error) {
        return <ApiErrorAlert error={error} />;
    }

    const visibleFields = Object.entries(entityDtoConfig || {}).filter(([fieldName]) => !fieldsToHide?.includes(fieldName));

    return (
        <>
            <ApiErrorAlert error={error} />
            <Row gutter={[24, 0]}>
            {visibleFields.map(([fieldName, fieldProps]) => {
                const isFullWidth = FULL_WIDTH_FIELDS.has(fieldName) || fieldProps.type === 'List`1' || fieldProps.type === 'TemplateContent';
                const colSpan = isFullWidth
                    ? { xs: 24, sm: 24, md: 24, lg: 24, xl: 24 }
                    : { xs: 24, sm: 12, md: 12, lg: 8, xl: 8 };
                const value = entity[convertKeyToCamelCase(fieldName)];
                return (
                    <Col key={fieldName} {...colSpan}>
                        <ViewField label={t(cleanLabel(fieldName))} token={token}>
                            {renderViewValue(fieldName, fieldProps, value, userInfo, t)}
                        </ViewField>
                    </Col>
                );
            })}
            </Row>
        </>
    );
};

export default ViewForm;
